<template>
  <div v-if="traderSite" class="flex flex-col h-full">
    <BaseDetailsPreviewEditHeader
      :title="traderSiteTitle"
      link-name="trader-sites"
      :is-edit-active="modifiedTraderSite?.isFromManager || false"
      hide-layout-options
      :mode="mode"
      :is-modified="isModified"
      @edit-mode="mode = 'edit'"
      @cancel="onCancelEditMode"
      @save="onSaveChanges" />
    <TraderSiteDetails
      v-model="modifiedTraderSite"
      :mode="mode"
      :is-horizontal-layout="isHorizontalLayout"
      :validation="$v" />
  </div>
  <BaseDrawerConfirm
    :is-active="confirmModalActive"
    @cancel="confirmModalActive = false"
    @confirm="
      onDiscardChanges();
      isBackMode ? router.push('/trader-sites') : '';
    " />
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { type TraderSite } from '@/types/Traders';
import { getTraderSiteById, editTraderSite } from '@/services/api/traders';
import { useToastStore } from '@/stores/toast';
import { useRoute, useRouter } from 'vue-router';

import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import TraderSiteDetails from '@/components/traderSites/TraderSiteDetails.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';

import { useI18n } from 'vue-i18n';
import { deepCopy } from '@/utils/data';
import useVuelidate from '@vuelidate/core';
import { email, helpers, required } from '@vuelidate/validators';

const { t: $t } = useI18n({ useScope: 'global' });

const toastStore = useToastStore();
const route = useRoute();
const router = useRouter();
const traderSite = ref<TraderSite>();
const modifiedTraderSite = ref<TraderSite>();
const isHorizontalLayout = ref(false);
const mode = ref<'view' | 'edit'>('view');

onMounted(async () => {
  const res = await getTraderSiteById(route.params.id as string);
  if (res) traderSite.value = res as TraderSite;
  if (traderSite.value) modifiedTraderSite.value = deepCopy<TraderSite>(traderSite.value);
});

const traderSiteTitle = computed(() => {
  if (modifiedTraderSite.value) {
    if (modifiedTraderSite.value.name && modifiedTraderSite.value.code)
      return `${modifiedTraderSite.value.code} - ${modifiedTraderSite.value.name} `;
    else return `${modifiedTraderSite.value.code} - Trader site`;
  }
  return '-';
});

const confirmModalActive = ref(false);
const isBackMode = ref(false);

async function onCancelEditMode(backMode: string) {
  if (isModified.value) {
    confirmModalActive.value = true;
    if (backMode == 'back') {
      isBackMode.value = true;
    }
  } else mode.value = 'view';
}

const isModified = ref<boolean>(false);

function onDiscardChanges() {
  confirmModalActive.value = false;
  mode.value = 'view';
  if (traderSite.value) modifiedTraderSite.value = deepCopy<TraderSite>(traderSite.value);
}

watch(
  () => modifiedTraderSite.value,
  (newData) => {
    isModified.value = JSON.stringify(newData) !== JSON.stringify(traderSite.value);
    validationModel.value.vatLevel = newData?.vatLevelId || '';
    validationModel.value.email = newData?.email || '';
  },
  { deep: true }
);

async function onSaveChanges() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  if (!modifiedTraderSite.value) return;
  const res = await editTraderSite(modifiedTraderSite.value);
  isModified.value = false;
  res
    ? toastStore.success({ text: `${$t('common.toast.success')}` })
    : toastStore.error({ text: res });
  mode.value = 'view';
}

const validationModel = ref({
  vatLevel: modifiedTraderSite.value?.vatLevelId || '',
  email: modifiedTraderSite.value?.email || '',
});

const rules = computed(() => ({
  vatLevel: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  email: { email: helpers.withMessage($t('messages.errors.invalidEmail'), email) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });
</script>
